import { z } from 'zod';

import {
  bidTimeoutSecondsSchema,
  callIntervalSecondsSchema,
  type AuctionSettingsPayload,
  type AuctionStatusValue,
} from './auction.ts';
import {
  bidIncrementSchema,
  MANUAL_ASSIGNMENT_MIN_AMOUNT,
  type ActorRef,
  type AuctionEventTypeValue,
} from './messaging.ts';
import { playerRoleSchema, type PlayerRoleCounts, type PlayerRoleValue } from './player.ts';
import type { AuctionPlayerStatusValue } from './sequence.ts';

/**
 * Contratto Socket.IO: nomi degli eventi, room e payload condivisi fra
 * gateway e browser.
 *
 * I payload **client → server** hanno uno schema Zod: il gateway non si fida
 * mai di ciò che arriva da un browser. I payload **server → client** sono solo
 * tipi: li costruisce il server, e il client non ha nulla da validare.
 */

// ---------------------------------------------------------------------------
// Room
// ---------------------------------------------------------------------------

/** Ogni asta ha la propria room: l'isolamento realtime parte da qui. */
export function auctionRoom(auctionId: string): string {
  return `auction:${auctionId}`;
}

/**
 * Room dei soli amministratori dell'asta.
 *
 * Esiste perché qualcosa va a chi conduce e a nessun altro: il registro dei
 * rifiuti d'offerta. Il vantaggio informativo che l'instradamento privato
 * protegge è fra rivali — sapere che un avversario è rimasto senza budget —
 * mentre chi conduce l'asta deve poter spiegare perché un rialzo non è entrato.
 */
export function adminRoom(auctionId: string): string {
  return `${auctionRoom(auctionId)}:admin`;
}

// ---------------------------------------------------------------------------
// Handshake
// ---------------------------------------------------------------------------

/**
 * Identità richiesta dal client nell'handshake (`auth.as`).
 *
 * I due cookie di sessione hanno nomi diversi e **coesistono** nello stesso
 * browser: un amministratore che apre l'asta come fantallenatore per provarla
 * li ha entrambi. Senza una richiesta esplicita il gateway dovrebbe indovinare,
 * e qualunque ordine sceglierebbe sarebbe sbagliato per una delle due schede.
 *
 * È una **preferenza, non un permesso**: il gateway sceglie fra i cookie che
 * il browser ha davvero mandato e li verifica come sempre. Chiedere `admin`
 * senza esserlo non fa entrare nessuno.
 */
export const SOCKET_AUTH_ROLES = ['admin', 'participant'] as const;

export type SocketAuthRole = (typeof SOCKET_AUTH_ROLES)[number];

export function isSocketAuthRole(value: unknown): value is SocketAuthRole {
  return typeof value === 'string' && SOCKET_AUTH_ROLES.includes(value as SocketAuthRole);
}

// ---------------------------------------------------------------------------
// Client → Server
// ---------------------------------------------------------------------------

export const joinAuctionPayloadSchema = z.object({
  auctionId: z.uuid(),
});

export type JoinAuctionPayload = z.output<typeof joinAuctionPayloadSchema>;

export const leaveAuctionPayloadSchema = joinAuctionPayloadSchema;

export type LeaveAuctionPayload = z.output<typeof leaveAuctionPayloadSchema>;

/**
 * Offerta. Il client dichiara solo l'incremento: il prezzo lo calcola il
 * server. `commandId` è generato dal client e rende il comando idempotente
 * (retry, doppio click, riconnessione).
 *
 * `expectedHighestBid` è l'offerta corrente che il fantallenatore aveva sotto
 * gli occhi quando ha cliccato (`null` = «non aveva ancora offerto nessuno»).
 * Sommandoci l'incremento il processor ottiene la cifra scritta sul pulsante,
 * ed è quella che viene offerta: «+1» su 9 vale 10 anche se nel frattempo il
 * prezzo si è mosso, mai 11. Se il prezzo è salito oltre, l'offerta passa se la
 * batte ancora («+5» su 9 vale 14 e supera un 10 arrivato prima) e viene
 * rifiutata se non basta più.
 */
export const placeBidPayloadSchema = z.object({
  auctionId: z.uuid(),
  playerId: z.uuid(),
  increment: bidIncrementSchema,
  expectedHighestBid: z.number().int().min(0).nullable(),
  commandId: z.uuid(),
});

export type PlaceBidPayload = z.output<typeof placeBidPayloadSchema>;

export const adminCommandPayloadSchema = z.object({
  auctionId: z.uuid(),
  commandId: z.uuid(),
});

export type AdminCommandPayload = z.output<typeof adminCommandPayloadSchema>;

/**
 * Porta l'asta su un reparto: è anche il modo in cui l'amministratore la avvia,
 * scegliendo da dove cominciare.
 */
export const startRolePayloadSchema = adminCommandPayloadSchema.extend({
  role: playerRoleSchema,
});

export type StartRolePayload = z.output<typeof startRolePayloadSchema>;

/**
 * Riporta l'asta a una riga del registro: l'amministratore indica il punto,
 * non quanto tornare indietro.
 */
export const rollbackToEventPayloadSchema = adminCommandPayloadSchema.extend({
  eventId: z.uuid(),
});

export type RollbackToEventPayload = z.output<typeof rollbackToEventPayloadSchema>;

export const undoPurchasePayloadSchema = adminCommandPayloadSchema.extend({
  purchaseId: z.uuid(),
});

export type UndoPurchasePayload = z.output<typeof undoPurchasePayloadSchema>;

/**
 * Assegnazione manuale: calciatore, fantallenatore e prezzo scelti
 * dall'amministratore. Il prezzo viaggia dal client perché qui è una
 * decisione, non un calcolo — ma è il processor a stabilire se sta in piedi.
 */
export const assignPlayerPayloadSchema = adminCommandPayloadSchema.extend({
  playerId: z.uuid(),
  participantId: z.uuid(),
  amount: z.number().int().min(MANUAL_ASSIGNMENT_MIN_AMOUNT),
});

export type AssignPlayerPayload = z.output<typeof assignPlayerPayloadSchema>;

/**
 * I due tempi del motore, cambiati a asta in pausa.
 *
 * Stessi limiti della configurazione REST: gli schemi sono gli stessi oggetti,
 * non due copie che possono divergere.
 */
export const updateTimingPayloadSchema = adminCommandPayloadSchema.extend({
  bidTimeoutSeconds: bidTimeoutSecondsSchema,
  callIntervalSeconds: callIntervalSecondsSchema,
});

export type UpdateTimingPayload = z.output<typeof updateTimingPayloadSchema>;

// ---------------------------------------------------------------------------
// Ack
// ---------------------------------------------------------------------------

/**
 * Risposta immediata a un comando via socket.
 *
 * `accepted` significa "preso in carico", non "eseguito": l'esito vero arriva
 * come evento broadcast (`bid:accepted`, `auction:status`, ...). Un rifiuto
 * qui è sincrono: payload malformato, autorizzazione mancante, room sbagliata.
 */
export type SocketCommandAck =
  | { readonly accepted: true }
  | { readonly accepted: false; readonly code: string; readonly message: string };

// ---------------------------------------------------------------------------
// Server → Client: payload
// ---------------------------------------------------------------------------

/** Calciatore così come lo vedono tutti i client durante la chiamata. */
export interface CalledPlayerPayload {
  readonly playerId: string;
  readonly externalId: string;
  readonly name: string;
  readonly team: string;
  readonly role: PlayerRoleValue;
  /** Quotazione informativa del file Excel, non il prezzo di partenza. */
  readonly value: number;
  readonly sequenceNumber: number;
  readonly status: AuctionPlayerStatusValue;
}

export interface HighestBidPayload {
  readonly amount: number;
  readonly participantId: string;
  readonly participantName: string;
}

/** Countdown: il client disegna, il server decide. */
export interface CountdownPayload {
  /** ISO-8601: istante autorevole di scadenza, `null` se nessun countdown. */
  readonly deadline: string | null;
  /** ISO-8601: orologio del server al momento dell'invio, per compensare lo skew. */
  readonly serverTime: string;
}

export interface ParticipantPublicState {
  readonly participantId: string;
  readonly displayName: string;
  readonly initialBudget: number;
  readonly remainingBudget: number;
  readonly purchaseCount: number;
  readonly online: boolean;
}

export interface PurchasePayload {
  readonly purchaseId: string;
  readonly playerId: string;
  readonly playerName: string;
  readonly playerTeam: string;
  readonly playerRole: PlayerRoleValue;
  readonly participantId: string;
  readonly participantName: string;
  readonly amount: number;
  /** ISO-8601. */
  readonly createdAt: string;
}

export interface SequenceProgressPayload {
  /** Posizione corrente 1-based nella sequenza, `null` prima dell'avvio. */
  readonly currentPosition: number | null;
  readonly total: number;
}

/**
 * Un calciatore dell'asta con il suo esito, nell'ordine di chiamata.
 *
 * Va a **tutti**, amministratore e fantallenatori — ma solo dove l'ordine è
 * dichiarato: nei reparti alfabetici l'elenco completo è una scelta esplicita
 * del committente, mentre dove l'ordine è casuale (modalità `RANDOM`, o un
 * reparto su `START_LETTER_RANDOM`) i calciatori **non ancora chiamati** non
 * compaiono — i prossimi devono restare una sorpresa per chiunque. Il punto
 * che applica la regola è uno solo: il filtro nello `SnapshotService`.
 */
export interface AuctionPlayerEntry {
  readonly playerId: string;
  readonly name: string;
  readonly team: string;
  readonly role: PlayerRoleValue;
  readonly sequenceNumber: number;
  /** `PENDING` finché non viene chiamato la prima volta. */
  readonly status: AuctionPlayerStatusValue;
  /**
   * Quando la sua ultima chiamata si è chiusa. ISO-8601, `null` se mai chiamato.
   *
   * Non è decorazione: è l'**ordine del giro degli invenduti**. Finito il giro
   * dei mai chiamati, l'asta richiama gli invenduti dal più vecchio, e senza
   * questo dato il client non può che riordinarli per sequenza — cioè indicare
   * per sempre il primo nome dell'elenco come prossimo, mentre il processor
   * chiama qualcun altro (`selectNextCall` in `@falasta/auction-domain`
   * documenta perché l'ordine per sequenza bloccherebbe l'asta sul primo nome).
   */
  readonly resolvedAt: string | null;
  /** Prezzo di aggiudicazione, `null` se non è (più) venduto. */
  readonly amount: number | null;
  readonly participantName: string | null;
}

/**
 * Fotografia completa dell'asta, inviata a ogni join/rejoin.
 *
 * Un client non ricostruisce mai lo stato dagli eventi precedenti: entra,
 * riceve lo snapshot e da lì applica gli eventi successivi.
 */
export interface AuctionSnapshotPayload {
  readonly auctionId: string;
  readonly name: string;
  readonly status: AuctionStatusValue;
  readonly settings: AuctionSettingsPayload;
  /**
   * Il reparto su cui l'asta sta lavorando, `null` prima dell'avvio.
   *
   * Non si ricava dal calciatore in chiamata: fra una chiamata e l'altra non
   * c'è nessun calciatore, e il reparto invece continua a essere quello.
   */
  readonly currentRole: PlayerRoleValue | null;
  readonly currentPlayer: CalledPlayerPayload | null;
  /**
   * I calciatori dell'asta in ordine di chiamata, con il loro esito — senza i
   * non ancora chiamati delle porzioni a ordine casuale (vedi
   * `AuctionPlayerEntry`).
   */
  readonly players: readonly AuctionPlayerEntry[];
  /**
   * Quanti calciatori per ruolo compone l'intera sequenza, **nascosti
   * compresi**: la composizione della rosa non è un segreto, l'ordine sì. È
   * ciò che permette al client di sapere se un reparto ha ancora calciatori
   * anche quando i suoi nomi non sono in elenco.
   */
  readonly roleCounts: PlayerRoleCounts;
  readonly highestBid: HighestBidPayload | null;
  readonly countdown: CountdownPayload;
  /** Millisecondi residui del countdown congelati dalla pausa, `null` altrimenti. */
  readonly pausedRemainingMs: number | null;
  readonly participants: readonly ParticipantPublicState[];
  readonly purchases: readonly PurchasePayload[];
  readonly progress: SequenceProgressPayload;
}

export interface AuctionStatusChangedPayload {
  readonly auctionId: string;
  readonly status: AuctionStatusValue;
  /** ISO-8601. */
  readonly changedAt: string;
}

/**
 * I tempi del motore sono cambiati a asta in corso.
 *
 * Va a **tutti**, non solo a chi conduce: il countdown che il fantallenatore
 * vede scorrere è disegnato sulla durata dello snapshot, e senza questo evento
 * resterebbe tarato sul valore vecchio fino al prossimo rientro nell'asta —
 * una barra che si svuota a metà chiamata, o che non si svuota mai.
 */
export interface AuctionTimingChangedPayload {
  readonly auctionId: string;
  readonly bidTimeoutSeconds: number;
  readonly callIntervalSeconds: number;
  readonly eventId: string;
}

export interface PlayerCalledPayload {
  readonly auctionId: string;
  readonly player: CalledPlayerPayload;
  readonly countdown: CountdownPayload;
  readonly eventId: string;
}

export interface PlayerUnsoldPayload {
  readonly auctionId: string;
  readonly playerId: string;
  readonly eventId: string;
}

export interface BidAcceptedPayload {
  readonly auctionId: string;
  readonly playerId: string;
  readonly commandId: string;
  readonly bid: HighestBidPayload & { readonly increment: number };
  readonly countdown: CountdownPayload;
  readonly eventId: string;
}

/** Inviato solo all'autore dell'offerta rifiutata, mai in broadcast. */
export interface BidRejectedPayload {
  readonly auctionId: string;
  readonly playerId: string;
  readonly commandId: string;
  readonly code: string;
  readonly message: string;
}

/**
 * Lo stesso rifiuto, ma come **voce di registro**: chi ci ha provato, con che
 * rialzo, quando e perché non è entrato.
 *
 * Due eventi per lo stesso fatto, come `bid:accepted` e `bid:newHighest`:
 * `bid:rejected` è la risposta al pulsante premuto (lampeggia e sparisce),
 * questo è la riga che resta nel dettaglio della chiamata.
 *
 * Non porta un importo: un'offerta rifiutata non ha prezzo, ha un tentativo.
 * Scrivere una cifra che non è mai esistita renderebbe la riga indistinguibile
 * da un rialzo valido.
 *
 * Va **solo** alla room amministratore e alla room privata dell'autore: sul
 * flusso generico è vietato (vedi `PRIVATE_EVENT_TYPES` nel gateway).
 */
export interface BidRejectedEntryPayload {
  readonly auctionId: string;
  readonly playerId: string;
  readonly commandId: string;
  readonly participantId: string;
  readonly participantName: string;
  readonly increment: number;
  readonly code: string;
  readonly message: string;
  /**
   * ISO-8601, orologio del server al momento dell'invio: **la stessa base** di
   * `countdown.serverTime` delle offerte accettate, perché le due liste finiscono
   * ordinate insieme nello stesso registro.
   */
  readonly serverTime: string;
  readonly eventId: string;
}

export interface PurchaseCreatedPayload {
  readonly auctionId: string;
  readonly purchase: PurchasePayload;
  /** Budget residuo aggiornato dell'acquirente. */
  readonly remainingBudget: number;
  readonly eventId: string;
}

export interface PurchaseRevertedPayload {
  readonly auctionId: string;
  readonly purchaseId: string;
  readonly playerId: string;
  readonly participantId: string;
  /** Budget residuo del partecipante dopo la restituzione. */
  readonly remainingBudget: number;
  readonly eventId: string;
}

export interface ParticipantPresencePayload {
  readonly auctionId: string;
  readonly participantId: string;
  readonly displayName: string;
  readonly online: boolean;
}

export interface AuctionErrorPayload {
  readonly auctionId: string | null;
  readonly code: string;
  readonly message: string;
}

/** Evento generico di audit rilanciato ai client (toast, log realtime). */
export interface AuctionEventBroadcastPayload {
  readonly auctionId: string;
  readonly eventId: string;
  readonly type: AuctionEventTypeValue;
  readonly actor: ActorRef;
  /** ISO-8601. */
  readonly occurredAt: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Mappe degli eventi
// ---------------------------------------------------------------------------

export interface ClientToServerEvents {
  'auction:join': (payload: JoinAuctionPayload, ack: (result: SocketCommandAck) => void) => void;
  'auction:leave': (payload: LeaveAuctionPayload, ack: (result: SocketCommandAck) => void) => void;
  'bid:place': (payload: PlaceBidPayload, ack: (result: SocketCommandAck) => void) => void;
  'admin:auction:start': (
    payload: AdminCommandPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:auction:pause': (
    payload: AdminCommandPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:auction:resume': (
    payload: AdminCommandPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:auction:stop': (
    payload: AdminCommandPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:auction:finish': (
    payload: AdminCommandPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:call:restart': (
    payload: AdminCommandPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:auction:rollbackTo': (
    payload: RollbackToEventPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:role:start': (payload: StartRolePayload, ack: (result: SocketCommandAck) => void) => void;
  'admin:purchase:undo': (
    payload: UndoPurchasePayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:player:assign': (
    payload: AssignPlayerPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
  'admin:settings:timing': (
    payload: UpdateTimingPayload,
    ack: (result: SocketCommandAck) => void,
  ) => void;
}

export interface ServerToClientEvents {
  'auction:snapshot': (payload: AuctionSnapshotPayload) => void;
  'auction:status': (payload: AuctionStatusChangedPayload) => void;
  'auction:timing': (payload: AuctionTimingChangedPayload) => void;
  'auction:event': (payload: AuctionEventBroadcastPayload) => void;
  'player:called': (payload: PlayerCalledPayload) => void;
  'player:unsold': (payload: PlayerUnsoldPayload) => void;
  'bid:accepted': (payload: BidAcceptedPayload) => void;
  'bid:rejected': (payload: BidRejectedPayload) => void;
  'bid:rejectedEntry': (payload: BidRejectedEntryPayload) => void;
  'bid:newHighest': (payload: BidAcceptedPayload) => void;
  'purchase:created': (payload: PurchaseCreatedPayload) => void;
  'purchase:reverted': (payload: PurchaseRevertedPayload) => void;
  'participant:joined': (payload: ParticipantPresencePayload) => void;
  'participant:left': (payload: ParticipantPresencePayload) => void;
  'auction:error': (payload: AuctionErrorPayload) => void;
}

/**
 * Elenchi runtime dei nomi. `satisfies` garantisce che ogni voce esista nella
 * mappa; l'esaustività inversa è verificata più sotto.
 */
export const CLIENT_TO_SERVER_EVENT_NAMES = [
  'auction:join',
  'auction:leave',
  'bid:place',
  'admin:auction:start',
  'admin:auction:pause',
  'admin:auction:resume',
  'admin:auction:stop',
  'admin:auction:finish',
  'admin:call:restart',
  'admin:auction:rollbackTo',
  'admin:role:start',
  'admin:purchase:undo',
  'admin:player:assign',
  'admin:settings:timing',
] as const satisfies readonly (keyof ClientToServerEvents)[];

export const SERVER_TO_CLIENT_EVENT_NAMES = [
  'auction:snapshot',
  'auction:status',
  'auction:timing',
  'auction:event',
  'player:called',
  'player:unsold',
  'bid:accepted',
  'bid:rejected',
  'bid:rejectedEntry',
  'bid:newHighest',
  'purchase:created',
  'purchase:reverted',
  'participant:joined',
  'participant:left',
  'auction:error',
] as const satisfies readonly (keyof ServerToClientEvents)[];

/**
 * Se un evento viene aggiunto a una mappa ma non alla lista corrispondente,
 * l'`Exclude` smette di essere `never` e la riga non compila.
 */
export const CLIENT_EVENT_NAMES_ARE_EXHAUSTIVE: Exclude<
  keyof ClientToServerEvents,
  (typeof CLIENT_TO_SERVER_EVENT_NAMES)[number]
> extends never
  ? true
  : never = true;

export const SERVER_EVENT_NAMES_ARE_EXHAUSTIVE: Exclude<
  keyof ServerToClientEvents,
  (typeof SERVER_TO_CLIENT_EVENT_NAMES)[number]
> extends never
  ? true
  : never = true;
